import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import type { Recipe } from '../../../services/recipes/recipesService';
import { ALLERGIES, Allergy, toTranslationKey } from '../../../types/RecipeEnums';
import { ALLERGEN_SUBTYPES } from '../../../types/RecipeAllergens';
import { useRecipeViewer } from './RecipeViewerContext';
import { ViewerBack, ViewerLink, useViewerScroll } from './viewerNavigation';
import { recipeName } from './recipeViewerRecipeUtils';
import { allergenCellCode, allergenDeclaration, allergenSearchMatches, classifyAllergenSelection, SUBTYPE_CODES, validAllergenSelection } from './recipeViewerAllergenUtils';
import RecipeAllergenSummary, { AllergenIcon, RecordedAllergens } from '../../recipes/RecipeAllergenSummary';
import './recipeViewerAllergies.css';

function labelKey(value: string) {
  return ALLERGIES.includes(value as Allergy)
    ? `recipeViewer.allergenNames.${toTranslationKey(value)}` : `recipeViewer.allergenSubtypes.${value}`;
}

function useAllergyFilters() {
  const [params, setParams] = useSearchParams();
  const { recipes } = useRecipeViewer();
  const { t } = useTranslation();
  const search = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const selected = params.getAll('allergen').filter(validAllergenSelection);
  const update = (key: string, values: string[]) => setParams(previous => {
    const next = new URLSearchParams(previous);
    next.delete(key);
    values.filter(Boolean).forEach(value => next.append(key, value));
    next.delete('limit');
    return next;
  }, { replace: true });
  const filtered = useMemo(() => recipes.filter(recipe => (!category ||
    (recipe.categoryId ?? recipe.category) === category) && allergenSearchMatches(recipe, search, key => t(labelKey(key)))),
  [recipes, category, search, t]);
  const categories = useMemo(() => Array.from(new Map(recipes.filter(recipe => recipe.category)
    .map(recipe => [recipe.categoryId ?? recipe.category!, recipe.category!])).entries())
    .sort((a, b) => a[1].localeCompare(b[1])), [recipes]);
  return { search, category, selected, update, filtered, categories, params };
}

function AllergyFilters({ filters }: { filters: ReturnType<typeof useAllergyFilters> }) {
  const { t } = useTranslation();
  const { search, category, selected, update, categories } = filters;
  return <div className="rv-allergy-controls">
    <div className="rv-allergy-search-row">
      <label className="rv-allergy-search-label"><span>{t('recipeViewer.allergyTextSearch')}</span>
        <input className="rv-input" type="search" value={search} placeholder={t('recipeViewer.allergySearchPlaceholder')}
          onChange={event => update('q', [event.target.value])} />
      </label>
      <label><span>{t('recipeViewer.allergyCategory')}</span>
        <select className="rv-input" value={category} onChange={event => update('category', [event.target.value])}>
          <option value="">{t('recipeViewer.allergyAllCategories')}</option>
          {categories.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </label>
    </div>
    <section className="rv-allergen-filters" aria-label={t('recipeViewer.allergyFilterDescription')}>
      <div className="rv-allergen-filter-heading">
        <h2>{t('recipeViewer.allergyFilters', { count: selected.length })}</h2>
        {!!selected.length && <button type="button" className="rv-allergen-clear" onClick={() => update('allergen', [])}>{t('recipeViewer.allergyClearFilters')}</button>}
      </div>
      <p className="rv-muted">{t('recipeViewer.allergyFilterDescription')}</p>
      <div className="rv-allergen-options" role="group" aria-label={t('recipeViewer.allergyFilterDescription')}>
        {ALLERGIES.flatMap(parent => [parent, ...(ALLERGEN_SUBTYPES[parent] ?? []).map(subtype => `${parent}:${subtype}`)]).map(value => {
          const active = selected.includes(value);
          return <button key={value} type="button" className="rv-allergen-chip" aria-pressed={active}
            onClick={() => update('allergen', active ? selected.filter(item => item !== value) : [...selected, value])}>
            {!value.includes(':') && <AllergenIcon allergen={value as Allergy} />}
            {t(labelKey(value.split(':')[1] ?? value))}
            {active && <Check size={15} aria-hidden="true" />}
          </button>;
        })}
      </div>
    </section>
  </div>;
}

function declarationText(recipe: Recipe, parent: Allergy, stale: boolean, t: (key: string) => string) {
  const declaration = allergenDeclaration(recipe, parent, stale);
  if (declaration.state !== 'contains') return t('recipeViewer.allergyState.notRecorded');
  return [t('recipeViewer.allergyState.contains'), ...declaration.subtypes.map(value => t(labelKey(value)))].join(' · ');
}

export function ViewerDeclaration({ recipe, stale = false }: { recipe: Recipe; stale?: boolean }) {
  const { t } = useTranslation();
  return <section className="rv-declaration">
    <h2>{t('recipeViewer.allergyDeclaration')}</h2>
    {!!recipe.diets?.length && <p className="rv-muted">{recipe.diets.map(diet => t(`dietKeys.${toTranslationKey(diet)}`, { defaultValue: diet })).join(' · ')}</p>}
    <RecipeAllergenSummary recipe={recipe} stale={stale} showNotice={false} />
  </section>;
}

function AllergenLegend() {
  const { t } = useTranslation();
  return <details className="rv-allergy-legend">
    <summary>{t('recipeViewer.allergySubtypeLegend')}</summary>
    {Object.entries(ALLERGEN_SUBTYPES).map(([parent, values]) => <p key={parent}>
      <strong>{t(labelKey(parent))}: </strong>
      {values.map(value => `${SUBTYPE_CODES[value]}: ${t(labelKey(value))}`).join(' · ')}
    </p>)}
  </details>;
}

function MoreResults({ total, limit, onMore }: { total: number; limit: number; onMore: () => void }) {
  const { t } = useTranslation();
  return limit < total ? <button className="rv-button rv-allergy-more" onClick={onMore}>{t('recipeViewer.allergyShowMore')}</button> : null;
}

function AllergenCellDetail({ recipe, parent, stale, onClose }: { recipe: Recipe; parent: Allergy; stale: boolean; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const { t, i18n } = useTranslation();
  useEffect(() => { dialog.current?.showModal(); }, []);
  return <dialog className="rv-allergen-dialog" ref={dialog} aria-labelledby="rv-allergen-cell-title" onClose={onClose}>
    <h2 id="rv-allergen-cell-title">{recipeName(recipe, i18n.language)} — {t(labelKey(parent))}</h2>
    <p>{declarationText(recipe, parent, stale, t)}</p>
    <button className="rv-button" autoFocus onClick={() => dialog.current?.close()}>{t('recipeViewer.allergyCloseDetails')}</button>
  </dialog>;
}

export function RecipeViewerMatrix() {
  const data = useRecipeViewer();
  const { t, i18n } = useTranslation();
  const filters = useAllergyFilters();
  const [params, setParams] = useSearchParams();
  const [activeCell, setActiveCell] = useState<{ recipeId: string; parent: Allergy } | null>(null);
  useViewerScroll();
  useViewerScroll('recipeViewerMatrixScroll');
  const table = params.get('view') === 'table';
  const limit = Math.max(60, Number(params.get('limit')) || 60);
  const recipes = filters.filtered.filter(recipe => !filters.selected.length ||
    classifyAllergenSelection(recipe, filters.selected, data.isStale) === 'contains');
  const base = `/recipe-viewer/${data.restaurantId}`;
  const selectedRecipe = activeCell ? recipes.find(recipe => recipe.id === activeCell.recipeId) : undefined;
  const update = (key: string, value: string) => setParams(previous => {
    const next = new URLSearchParams(previous); next.set(key, value); return next;
  }, { replace: true });
  return <div className="rv-page rv-allergies">
    <h1>{t('recipeViewer.allergyMatrixTitle')}</h1>
    <AllergyFilters filters={filters} />
    <p className="rv-muted">{t('recipeViewer.allergyMatrixLegend')}</p>
    {!!filters.selected.length && <p className="rv-allergy-state-unknown">{t('recipeViewer.allergyMatrixFiltered')}</p>}
    <AllergenLegend />
    <button className="rv-button rv-allergy-view-toggle" onClick={() => update('view', table ? 'cards' : 'table')}>
      {t(table ? 'recipeViewer.allergyCompactView' : 'recipeViewer.allergyTableView')}
    </button>
    {!recipes.length ? <p className="rv-panel rv-allergy-empty">{t(data.recipes.length ? 'recipeViewer.allergyNoResults' : 'recipeViewer.allergyNoRecipes')}</p> : <>
      <div id="recipeViewerMatrixScroll" className={`rv-matrix-scroll ${table ? 'rv-matrix-forced' : ''}`} tabIndex={0} role="region" aria-label={t('recipeViewer.allergyMatrixTitle')}>
        <table className="rv-matrix">
          <thead><tr><th scope="col">{t('recipeViewer.allergyDish')}</th>{ALLERGIES.map(parent => <th scope="col" key={parent}><span className="rv-matrix-header"><AllergenIcon allergen={parent} />{t(labelKey(parent))}</span></th>)}</tr></thead>
          <tbody>{recipes.slice(0, limit).map(recipe => <tr key={recipe.id}>
            <th scope="row"><ViewerLink to={`${base}/allergies/recipes/${recipe.id}`}>{recipeName(recipe, i18n.language) || t('recipeViewer.allergyUntitledRecipe')}</ViewerLink></th>
            {ALLERGIES.map(parent => {
              const declaration = allergenDeclaration(recipe, parent, data.isStale);
              return <td key={parent}><button className={`rv-allergen-cell rv-allergy-state-${declaration.state}`}
                aria-label={`${recipeName(recipe, i18n.language)} — ${t(labelKey(parent))}: ${declarationText(recipe, parent, data.isStale, t)}`}
                aria-expanded={activeCell?.recipeId === recipe.id && activeCell.parent === parent}
                onClick={() => setActiveCell({ recipeId: recipe.id, parent })}>
                <span>{allergenCellCode(recipe, parent, data.isStale)}</span>
              </button></td>;
            })}
          </tr>)}</tbody>
        </table>
      </div>
      {activeCell && selectedRecipe && <AllergenCellDetail recipe={selectedRecipe} parent={activeCell.parent} stale={data.isStale} onClose={() => setActiveCell(null)} />}
      {!table && <div className="rv-matrix-cards">{recipes.slice(0, limit).map(recipe => <details className="rv-panel rv-matrix-card" key={recipe.id}
        open={params.get('expanded') === recipe.id}>
        <summary onClick={event => { event.preventDefault(); update('expanded', params.get('expanded') === recipe.id ? '' : recipe.id); }}>
          {recipeName(recipe, i18n.language) || t('recipeViewer.allergyUntitledRecipe')}
        </summary>
        <ViewerDeclaration recipe={recipe} stale={data.isStale} />
        <ViewerLink className="rv-button" to={`${base}/allergies/recipes/${recipe.id}`}>{t('recipeViewer.allergyViewDetails')}</ViewerLink>
      </details>)}</div>}
      <MoreResults total={recipes.length} limit={limit} onMore={() => update('limit', String(limit + 60))} />
    </>}
  </div>;
}

export function RecipeViewerAllergySearch() {
  const data = useRecipeViewer();
  const { t, i18n } = useTranslation();
  const filters = useAllergyFilters();
  const [params, setParams] = useSearchParams();
  const limit = Math.max(60, Number(params.get('limit')) || 60);
  useViewerScroll();
  return <div className="rv-page rv-allergies">
    <h1>{t('recipeViewer.allergySearchTitle')}</h1>
    <AllergyFilters filters={filters} />
    <div className="rv-allergy-results">
      {filters.filtered.slice(0, limit).map(recipe => {
        const state = classifyAllergenSelection(recipe, filters.selected, data.isStale);
        return <ViewerLink className="rv-panel rv-allergy-result" key={recipe.id} to={`/recipe-viewer/${data.restaurantId}/allergies/recipes/${recipe.id}`}>
          <h2>{recipeName(recipe, i18n.language) || t('recipeViewer.allergyUntitledRecipe')}</h2>
          {recipe.category && <span className="rv-muted">{recipe.category}</span>}
          <RecordedAllergens recipe={recipe} stale={data.isStale} />
          {state !== 'browse' && <span className={`rv-allergy-state-${state === 'contains' ? 'contains' : 'unknown'}`}>{t(`recipeViewer.allergySelection.${state === 'contains' ? 'contains' : 'notRecorded'}`)}</span>}
        </ViewerLink>;
      })}
    </div>
    {!filters.filtered.length && <p className="rv-panel rv-allergy-empty">{t(data.recipes.length ? 'recipeViewer.allergyNoResults' : 'recipeViewer.allergyNoRecipes')}</p>}
    <MoreResults total={filters.filtered.length} limit={limit} onMore={() => setParams(previous => {
      const next = new URLSearchParams(previous); next.set('limit', String(limit + 60)); return next;
    }, { replace: true })} />
  </div>;
}

export function RecipeViewerAllergenDetail() {
  const { recipeId } = useParams();
  const data = useRecipeViewer();
  const { t, i18n } = useTranslation();
  useViewerScroll();
  const recipe = data.recipes.find(item => item.id === recipeId && item.restaurantId === data.restaurantId);
  const base = `/recipe-viewer/${data.restaurantId}`;
  return <div className="rv-page rv-allergies rv-allergy-detail">
    <ViewerBack fallback={`${base}/allergies/matrix`} />
    {recipe ? <>
      <h1>{recipeName(recipe, i18n.language) || t('recipeViewer.allergyUntitledRecipe')}</h1>
      <div className="rv-panel rv-allergy-detail-panel"><ViewerDeclaration recipe={recipe} stale={data.isStale} />
      </div>
      <ViewerLink className="rv-button" to={`${base}/recipes/${recipe.id}`}>{t('recipeViewer.allergyViewRecipe')}</ViewerLink>
    </> : <div className="rv-panel rv-allergy-empty"><h1>{t('recipeViewer.allergyUnavailable')}</h1>
      <p className="rv-muted">{t('recipeViewer.allergyUnavailableDescription')}</p>
      <button className="rv-button" disabled={data.isRefreshing} onClick={data.refresh}>{t('recipeViewer.allergyRetry')}</button>
    </div>}
  </div>;
}
